import React from 'react';
import { refreshFlowToken } from '../../api/AuthService';
import {
  retrieveAuthTokens,
  retrieveFlowRefreshToken,
  storeFlowTokens,
} from '../../utils/next/auth.utils';

export interface FlowTokenInfo {
  /** Seconds since the epoch. */
  readonly exp: number;
  readonly wid: string | null;
}

const LEAD_MS = 60_000;
const MIN_MS = 5_000;

/** Reads exp and wid from the token without checking its signature; the server does that. */
export const decodeFlowToken = (token: string | null): FlowTokenInfo | null => {
  const payload = token?.split('.')[1];
  if (!payload) {
    return null;
  }
  try {
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const claims = JSON.parse(
      atob(base64.padEnd(Math.ceil(base64.length / 4) * 4, '='))
    );
    if (typeof claims?.exp !== 'number') {
      return null;
    }
    return {
      exp: claims.exp,
      wid: typeof claims.wid === 'string' ? claims.wid : null,
    };
  } catch {
    return null;
  }
};

export const msUntilRefresh = (
  expSeconds: number,
  nowMs: number,
  leadMs = LEAD_MS,
  minMs = MIN_MS
): number => {
  const remaining = expSeconds * 1000 - nowMs;
  if (remaining <= 0) {
    return 0;
  }
  return Math.max(minMs, remaining - leadMs);
};

type RefreshOutcome = 'ok' | 'rejected' | 'failed';

const isRejection = (error: unknown): boolean => {
  const status = (error as { statusCode?: number | null })?.statusCode;
  return status === 401 || status === 403;
};

const refreshOnce = async (): Promise<RefreshOutcome> => {
  const stored = retrieveFlowRefreshToken();
  if (!stored) {
    return 'rejected';
  }
  try {
    const { data } = await refreshFlowToken({ data: { refreshToken: stored } });
    if (!data?.accessToken) {
      return 'rejected';
    }
    storeFlowTokens(data.accessToken, data.refreshToken ?? stored);
    return 'ok';
  } catch (error) {
    return isRejection(error) ? 'rejected' : 'failed';
  }
};

let inFlight: Promise<RefreshOutcome> | null = null;

/**
 * Trades the flow's own refresh token for new tokens and stores them under the flow's keys, never
 * the staff session's. Callers that arrive while a refresh is running (the timer and a 401 retry)
 * share it: the server rotates refresh tokens, so a second POST with the old one would fail.
 */
export const refreshFlowOutcome = (): Promise<RefreshOutcome> => {
  if (!inFlight) {
    inFlight = refreshOnce().finally(() => {
      inFlight = null;
    });
  }
  return inFlight;
};

/** True when new tokens are stored. False means nothing changed. */
export const refreshFlowSession = async (): Promise<boolean> =>
  (await refreshFlowOutcome()) === 'ok';

/** On a 401 refreshes once and retries once. Any other failure, or a failed refresh, is the original error. */
export const withFlowRetry = async <T>(call: () => Promise<T>): Promise<T> => {
  try {
    return await call();
  } catch (error) {
    if ((error as { statusCode?: number })?.statusCode !== 401) {
      throw error;
    }
    if (!(await refreshFlowSession())) {
      throw error;
    }
    return call();
  }
};

const storedInfo = () =>
  decodeFlowToken(retrieveAuthTokens().accessToken ?? null);

/** Refreshes the session shortly before the access token expires, for as long as it keeps working. */
export const useFlowSessionTimer = (): {
  readonly workflowId: string | null;
} => {
  const [workflowId] = React.useState(() => storedInfo()?.wid ?? null);

  React.useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    let cancelled = false;

    const schedule = (delayMs: number, retriesLeft: number) => {
      if (cancelled) {
        return;
      }
      timer = setTimeout(async () => {
        const outcome = await refreshFlowOutcome();
        if (cancelled) {
          return;
        }
        if (outcome === 'ok') {
          // At least MIN_MS, so a token that is already expired by this clock (the clock is
          // ahead) cannot make refreshes run back to back.
          const info = storedInfo();
          if (info) {
            schedule(Math.max(MIN_MS, msUntilRefresh(info.exp, Date.now())), 1);
          }
        } else if (outcome === 'failed' && retriesLeft > 0) {
          // A network or server failure may pass; a rejection will not.
          schedule(MIN_MS, retriesLeft - 1);
        }
      }, delayMs);
    };

    const info = storedInfo();
    if (info) {
      schedule(msUntilRefresh(info.exp, Date.now()), 1);
    }
    return () => {
      cancelled = true;
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, []);

  return { workflowId };
};
